import { fleetPath } from '@/view/pathDefinitions';
import { useQueryClient } from '@tanstack/react-query';
import { atom, useAtom } from 'jotai';
import { useMatch } from 'react-router-dom';
import { useIsFeatureActive } from '../features';
import { continueConversation, startConversation } from './api';
import { ConversationHistoryDto, StartConversationRequest } from './api.types';
import { ChatMessage } from './types';
import { ChatContext, useChatContext } from './useChatContext';

interface ChatbotProps {
  chatContext: ChatContext;
  conversationId?: string;
  messages: ChatMessage[];
  isLoading: boolean;
  isAvailable: boolean;
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  handleUserInput: (text: string) => void;
  createNew: () => void;
  assignConversation: (conversation: ConversationHistoryDto) => void;
}

const isOpenAtom = atom<boolean>(false);
const isLoadingAtom = atom<boolean>(false);
const conversationIdAtom = atom<string | undefined>(undefined);
const messagesAtom = atom<ChatMessage[]>([]);
const nextId = atom<string>((get) => get(messagesAtom).length.toString());
const addMessageAtom = atom(null, (get, set, message: Partial<ChatMessage>) =>
  set(
    messagesAtom,
    get(messagesAtom).concat({
      ...message,
      id: get(nextId),
      timestamp: new Date(),
    } as ChatMessage)
  )
);

export const useChatbot = (): ChatbotProps => {
  const chatContext = useChatContext();
  const [conversationId, setConversationId] = useAtom(conversationIdAtom);
  const [messages, setMessages] = useAtom(messagesAtom);
  const [, addMessage] = useAtom(addMessageAtom);
  const [isOpenState, setIsOpen] = useAtom(isOpenAtom);
  const [isLoading, setIsLoading] = useAtom(isLoadingAtom);
  const isAvailable = useIsAvailable();
  const isOpen = isOpenState && isAvailable;
  const queryClient = useQueryClient();

  const handleUserInput = async (input: string) => {
    setIsLoading(true);
    addMessage({ sender: 'user', content: input });

    if (conversationId) {
      const apiResponse = await continueConversation(conversationId, input);
      addMessage({ sender: 'assistant', content: apiResponse.response });
    } else {
      const topicQuery: StartConversationRequest = {
        topicId: `manuals|${chatContext.equipmentIds[0]}`,
        query: input,
        embeddingOnly: false,
        translate: true,
        referenceLookupStrategy: 'tool',
        referenceType: 'pages',
      };

      const apiResponse = await startConversation(topicQuery);

      setConversationId(apiResponse.id);
      addMessage({ sender: 'assistant', content: apiResponse.response });
      queryClient.invalidateQueries({ queryKey: ['chatbotHistory'] });
    }

    setIsLoading(false);
  };

  const createNew = () => {
    setConversationId(undefined);
    setMessages([]);
  };

  const assignConversation = (conversation: ConversationHistoryDto) => {
    setConversationId(conversation.id);
    setMessages(
      conversation.history?.map((c, index) => ({
        id: index.toString(),
        sender: c.type === 'ai' ? 'assistant' : 'user',
        timestamp: new Date(c.createdAt),
        content: c.message!,
      })) ?? []
    );
  };

  return {
    chatContext,
    conversationId,
    messages,
    isLoading,
    isAvailable,
    isOpen,
    setIsOpen,
    handleUserInput,
    createNew,
    assignConversation,
  };
};

const useIsAvailable = () => {
  const isChatbotFeatureActive = useIsFeatureActive('chatbot');
  const isFleet = Boolean(useMatch(`${fleetPath}/*`));
  const isAvailable = isChatbotFeatureActive && isFleet;

  return isAvailable;
};
